import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./GroupsDialog.css";
import "./TravelersDialog.css";
import PromptModal from "./PromptModal";
import AlertModal from "./AlertModal";
import TravelerDocumentsDialog from "./TravelerDocumentsDialog";
import Common from "../../Common/Common";
import DataBase from "../../dataBase/liquidGuides";

// Flat CRUD list of family/travel-companion travelers, each holding their
// own set of documents. Structurally mirrors GroupsDialog.js's shell
// (grpdlg-* classes) but is user-scoped (props.travelers/onChange) instead of
// guide-scoped (window.guide_obj), since documents are long-lived personal
// records, not part of any one trip.
const TravelersDialog = ({ show, onHide, travelers, onChange, user_info, Authorization }) => {
  const [promptShow, setPromptShow] = useState(false);
  const [promptTitle, setPromptTitle] = useState("");
  const [promptMessage, setPromptMessage] = useState("");
  const [promptButtons, setPromptButtons] = useState([]);
  const [promptInputs, setPromptInputs] = useState([]);

  const [alertShow, setAlertShow] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertButtons, setAlertButtons] = useState([]);

  const [docsShow, setDocsShow] = useState(false);
  const [activeTravelerId, setActiveTravelerId] = useState(null);

  const list = travelers || [];
  const activeTraveler = list.find((t) => t.id === activeTravelerId) || null;

  const addTraveler = () => {
    setPromptTitle("Traveler");
    setPromptMessage("Enter Traveler Name");
    setPromptButtons([
      {
        Title: "OK",
        Method: (inputs) => {
          if (inputs === null || !inputs[0].value.trim()) return;
          onChange([...list, { id: `t_${Date.now()}`, name: inputs[0].value.trim(), documents: [] }]);
        },
      },
      { Title: "CANCEL", Method: null },
    ]);
    setPromptInputs([{ name: "Traveler Name", value: "" }]);
    setPromptShow(true);
  };

  const renameTraveler = (traveler) => {
    setPromptTitle("Traveler");
    setPromptMessage("Edit Traveler Name");
    setPromptButtons([
      {
        Title: "OK",
        Method: (inputs) => {
          if (inputs === null || !inputs[0].value.trim()) return;
          onChange(list.map((t) => (t.id === traveler.id ? { ...t, name: inputs[0].value.trim() } : t)));
        },
      },
      { Title: "CANCEL", Method: null },
    ]);
    setPromptInputs([{ name: "Traveler Name", value: traveler.name }]);
    setPromptShow(true);
  };

  const deleteTraveler = (traveler) => {
    setAlertTitle("Confirmation");
    setAlertMessage(`Confirm Delete ${traveler.name}? This removes all of their documents and attached files.`);
    setAlertButtons([
      {
        Title: "OK",
        Method: () => {
          (traveler.documents || []).forEach((doc) => {
            const context = `traveler_document:${traveler.id}:${doc.id}`;
            DataBase.getAttachments(context, user_info.uid, (res) => {
              if (!res.pass) return;
              res.res.forEach((att) => {
                const urls = [att.data.url, att.data.url_m].filter(Boolean);
                DataBase.deleteFromStorage(urls, () => {
                  DataBase.deleteAttachment(att.id, () => {});
                });
              });
            });
          });
          onChange(list.filter((t) => t.id !== traveler.id));
        },
      },
      { Title: "CANCEL", Method: null },
    ]);
    setAlertShow(true);
  };

  const openDocuments = (traveler) => {
    setActiveTravelerId(traveler.id);
    setDocsShow(true);
  };

  const travelerStatus = (traveler) => {
    const docs = traveler.documents || [];
    if (docs.some((d) => Common.getDocumentExpiryStatus(d.expiryDate) === "expired")) return "td-dot-red";
    if (docs.some((d) => Common.getDocumentExpiryStatus(d.expiryDate) === "soon")) return "td-dot-amber";
    return "td-dot-green";
  };

  const handleDocumentsChange = (updatedTraveler) => {
    onChange(list.map((t) => (t.id === updatedTraveler.id ? updatedTraveler : t)));
  };

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="grpdlg-dialog"
        aria-labelledby="contained-modal-title-vcenter"
        centered={false}
        scrollable={false}
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="grpdlg-shell">
            <div className="grpdlg-header">
              <div className="grpdlg-header-icon">
                <FontAwesomeIcon icon={solid("passport")} />
              </div>
              <h2 className="grpdlg-title">Travel Documents</h2>
              <button className="grpdlg-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="grpdlg-search-row">
              <div className="td-doc-count">
                {list.length} {list.length === 1 ? "traveler" : "travelers"}
              </div>
              <button className="grpdlg-add-btn" title="Add traveler" onClick={addTraveler}>
                <FontAwesomeIcon icon={solid("plus")} />
              </button>
            </div>

            <div className="grpdlg-list">
              {list.length === 0 && (
                <div className="td-empty">Add family members or travel companions to track their passports, visas, and other documents.</div>
              )}
              {list.map((traveler) => {
                const docCount = (traveler.documents || []).length;
                return (
                  <div className="grpdlg-item" key={traveler.id}>
                    <div className="grpdlg-item-thumb" onClick={() => openDocuments(traveler)}>
                      <FontAwesomeIcon icon={solid("user")} />
                    </div>
                    <div className="grpdlg-item-info" onClick={() => openDocuments(traveler)} style={{ cursor: "pointer" }}>
                      <span className="grpdlg-item-label">{traveler.name}</span>
                      <span className="grpdlg-item-schedule">
                        {docCount === 0
                          ? "Tap to add documents"
                          : `${docCount} ${docCount === 1 ? "document" : "documents"} · Tap to view`}
                      </span>
                    </div>
                    <span className={`td-dot ${travelerStatus(traveler)}`} />
                    <div className="grpdlg-item-actions">
                      <button className="grpdlg-icon-btn" title="Rename" onClick={() => renameTraveler(traveler)}>
                        <FontAwesomeIcon icon={solid("pen")} />
                      </button>
                      <button className="grpdlg-icon-btn danger" title="Delete" onClick={() => deleteTraveler(traveler)}>
                        <FontAwesomeIcon icon={solid("trash")} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PromptModal
        show={promptShow}
        onHide={() => setPromptShow(false)}
        PromptModalTitle={promptTitle}
        PromptModalMessage={promptMessage}
        PromptModalButtons={promptButtons}
        PromptModalInputs={promptInputs}
      />
      <AlertModal
        show={alertShow}
        onHide={() => setAlertShow(false)}
        AlertModalTitle={alertTitle}
        AlertModalMessage={alertMessage}
        AlertModalButtons={alertButtons}
      />
      <TravelerDocumentsDialog
        show={docsShow}
        onHide={() => setDocsShow(false)}
        traveler={activeTraveler}
        onChange={handleDocumentsChange}
        user_info={user_info}
        Authorization={Authorization}
      />
    </div>
  );
};

export default TravelersDialog;
